import { PlanTrailActionContext, FileUploadData, ImageDestroyData, getMap, getLayerRegistry } from "./types";
import { getMarkersAndLineStringsFrom, getMarkersAndLineStringsListFrom } from "../../../../../utils/helpers/ImportFileHelper";
import { GpxImportError, GpxImportErrorCode } from "../../../../../utils/helpers/gpxImportError";
import { gpxImportErrorMessage } from "../../../../../utils/helpers/gpxImportErrorMessage";
import { importedWaypointToHikeHighlight } from "../../../../../utils/helpers/PoiToHikeHighlightHelper";
import { promptGpxImportChoice } from "../../../../trail_import_dialog/GpxImportChoiceDialogService";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { RouteFactory } from "../../../../../utils/models/Route";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { gpxFileToGeoJsonHelper } from "../../../../../utils/helpers/GpxFileToGeoJsonHelper";
import { insertEvenlySpacedControlPoints } from "./controlPointSpacing";
import { bbox as turfBbox } from "@turf/turf";
import { computeMapFitPadding } from "./multi-route";
import { Modal } from "bootstrap";
import { MapLayerCategory } from "../../../../../utils/layer-registry";
import {sanitizeCoords} from "../../../../../utils/sanitizeCoords";
import graphClient from "../../../../../utils/graphql/graph_client";
import { GenerateDirectUpload } from "../../../../../utils/graphql/planned-trail-graph";
import SparkMD5 from 'spark-md5';
import toGeoJson from '@mapbox/togeojson';


async function calculateMD5Base64(buffer: ArrayBuffer): Promise<string> {
  const spark = new SparkMD5.ArrayBuffer();
  spark.append(buffer);
  const hash = spark.end();
  const bytes = new Uint8Array(hash.match(/.{2}/g).map(byte => parseInt(byte, 16)));
  return btoa(String.fromCharCode(...bytes));
}

export const fileOperationsActions = {
  async importFile({ state, dispatch, commit }: PlanTrailActionContext, file: File) {
    try {
      const importedRoutes = await getMarkersAndLineStringsListFrom(file);
      if (importedRoutes.length === 0) {
        throw new GpxImportError(GpxImportErrorCode.NoRouteOrTrack);
      }

      // Standalone waypoints (GPX <wpt>, KML placemarks, GeoJSON Points) are file-level
      // and attached to the first parsed route (see ImportFileHelper).
      const importedWaypoints = importedRoutes.flatMap((route: any) => route.waypoints || []);

      let importRoute = true;
      let importWaypoints = importedWaypoints.length > 0;

      // Only ask when the file actually mixes both — a plain track or a plain
      // waypoint file has nothing to choose between.
      if (importedWaypoints.length > 0) {
        const choice = await promptGpxImportChoice(importedWaypoints.length);
        if (!choice) {
          // User cancelled the choice dialog — nothing has been committed yet.
          return;
        }
        importRoute = choice === 'route' || choice === 'both';
        importWaypoints = choice === 'waypoints' || choice === 'both';
      }

      const importedLineStrings: LineStringFeature[] = [];

      if (importRoute) {
        // If planner only contains empty placeholder routes, remove them before importing.
        const allExistingRoutes = Object.values(state.routes || {});
        const existingNonEmptyRoutes = allExistingRoutes.filter((route: any) => {
          return (route.lineStrings && route.lineStrings.length > 0) ||
            (route.markers && route.markers.length > 0);
        });
        if (existingNonEmptyRoutes.length === 0 && allExistingRoutes.length > 0) {
          allExistingRoutes.forEach((route: any) => {
            commit('removeRoute', route.id);
          });
        }

        // Create a route per imported linestring so multi-route files load in one go.
        const fileName = file.name.replace(/\.[^/.]+$/, "").trim() || "Imported route";

        importedRoutes.forEach((routeData, index) => {
          const routeName = importedRoutes.length === 1 ? fileName : `${fileName} ${index + 1}`;
          commit('createNewRoute', routeName);

          const newRoute = state.routes[state.activeRouteKey];
          newRoute.markers = routeData.markers;
          newRoute.lineStrings = routeData.lineStrings;
          // Seed each imported route with evenly-spaced control points (same formula as the
          // "Add control points" tool). Synchronous + offline — no per-point network/undo.
          insertEvenlySpacedControlPoints(newRoute);
          newRoute.updated_at = new Date();
          importedLineStrings.push(...newRoute.lineStrings);
        });

        // Imports leave everything DESELECTED — createNewRoute activates each
        // route as it's created, which would leave the last one selected
        commit('clearActiveRoute');

        // Re-render all routes on the map
        await dispatch('addAllRoutesToMap');
      }

      if (importWaypoints && importedWaypoints.length > 0) {
        importedWaypoints.forEach((wp: any) => commit('addHikeHighlight', importedWaypointToHikeHighlight(wp)));
        await dispatch('addHighlightsToMap');
      }

      // Calculate bbox from whatever was actually imported (route, waypoints, or both).
      const bboxFeatures: any[] = importedLineStrings.map(ls => ls.toGeoJson());
      if (importWaypoints && importedWaypoints.length > 0) {
        importedWaypoints.forEach((wp: any) => {
          bboxFeatures.push({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [wp.lng, wp.lat] } });
        });
      }

      // @ts-ignore
      const element = document.getElementById('trail-import-dialog');
      const modal = Modal.getInstance(element);
      modal?.hide();

      if (bboxFeatures.length > 0) {
        const featureCollection = { type: 'FeatureCollection', features: bboxFeatures };
        const bbox = turfBbox(featureCollection);
        // @ts-ignore
        const map = getMap();
        map.fitBounds(bbox, { padding: 20 });
      }
    } catch (e) {
      alert(gpxImportErrorMessage(e));
    }
  },

  async replaceRouteFile({ state, dispatch, commit }: PlanTrailActionContext, { file, routeId }: { file: File; routeId: string }) {
    try {
      const markersAndLineStrings = await getMarkersAndLineStringsFrom(file);

      // Get the route to replace
      const route = state.routes[routeId];
      if (!route) {
        throw new Error('Route not found');
      }

      // Store the existing route properties we want to keep
      const existingDay = route.day;
      const existingRouteStyle = route.route_style;
      const existingPosition = route.position;

      // Derive name from the GPX filename, stripping the extension
      const fileBaseName = file.name.replace(/\.[^/.]+$/, '').trim();

      // Replace markers and linestrings with imported data
      route.markers = markersAndLineStrings.markers;
      route.lineStrings = markersAndLineStrings.lineStrings;
      route.updated_at = new Date();

      // Apply filename as the new route name, falling back to existing if empty
      route.name = fileBaseName || route.name;
      route.day = existingDay;
      route.route_style = existingRouteStyle;
      route.position = existingPosition;

      // Switch to the replaced route
      commit('setActiveRoute', routeId);

      // Re-render all routes on the map
      await dispatch('addAllRoutesToMap');

      // The route itself is always replaced (that's the point of this action), but any
      // standalone waypoints in the replacement file are optional extra data — ask first
      // rather than silently adding highlights the user didn't ask for.
      const replacedWaypoints = (markersAndLineStrings as any).waypoints || [];
      if (replacedWaypoints.length > 0) {
        // eslint-disable-next-line no-restricted-globals
        const importWaypoints = confirm(
          `This file also contains ${replacedWaypoints.length} waypoint${replacedWaypoints.length === 1 ? '' : 's'}. Import them as hike highlights?`
        );
        if (importWaypoints) {
          replacedWaypoints.forEach((wp: any) => commit('addHikeHighlight', importedWaypointToHikeHighlight(wp)));
          await dispatch('addHighlightsToMap');
        }
      }

      // Calculate bbox from the replaced route's linestrings
      const featureCollection = {
        type: 'FeatureCollection',
        features: route.lineStrings.map(ls => ls.toGeoJson())
      };
      const bbox = turfBbox(featureCollection);
      // @ts-ignore
      const map = getMap();
      map.fitBounds(bbox, { padding: 20 });
    } catch (e) {
      alert(gpxImportErrorMessage(e));
    }
  },

  async importFromGeoJson({ state, commit, dispatch }: PlanTrailActionContext, {
    geoJson,
    trailName,
    plannedTrailId,
    referencedHikes,
    day,
    route_style
  }: {
    geoJson: any,
    trailName?: string,
    plannedTrailId?: string,
    referencedHikes?: Array<{ id: string; name: string; slug: string }>,
    day?: number,
    route_style?: 'route' | 'alt' | 'spur' | 'transport'
  }) {
    // Extract the trail name from GeoJSON properties
    if (geoJson.properties && geoJson.properties.name) {
      trailName = geoJson.properties.name;
    } else if (geoJson.features && geoJson.features.length > 0) {
      // Check first feature for name
      const firstFeature = geoJson.features[0];
      if (firstFeature.properties && firstFeature.properties.name) {
        trailName = firstFeature.properties.name;
      } else if (firstFeature.properties && firstFeature.properties.title) {
        trailName = firstFeature.properties.title;
      }
    }

    // Create markers and linestrings arrays for the new route
    let markers: MarkerFeature[] = [];
    let lineStrings: LineStringFeature[] = [];

    if (geoJson.features.length > 1) {
      const markerFeatures = geoJson.features.filter((feature: any) => feature.geometry.type == 'Point');
      const lineStringFeatures = geoJson.features.filter((feature: any) => feature.geometry.type == 'LineString');

      lineStrings = lineStringFeatures.map((lineString: any) =>
        LineStringFeature.coordsOnlyFeature(lineString.geometry.coordinates)
      );

      markerFeatures.forEach((marker: any) => {
        const markerFeature = new MarkerFeature(
          new MarkerGeometry([marker.geometry.coordinates[0], marker.geometry.coordinates[1]]),
          new MarkerProperties(generateID(), true, Date.now().toString(), 0)
        );
        markers.push(markerFeature);
      });
    } else {
      const coordinates = geoJson.features[0].geometry.coordinates;
      const firstCoordinate = coordinates[0];
      const lastCoordinate = coordinates[coordinates.length - 1];

      // Create start marker
      const startMarker = new MarkerFeature(
        new MarkerGeometry([firstCoordinate[0], firstCoordinate[1]]),
        new MarkerProperties(generateID(), true, Date.now().toString(), 0)
      );
      markers.push(startMarker);

      // Create end marker
      const endMarker = new MarkerFeature(
        new MarkerGeometry([lastCoordinate[0], lastCoordinate[1]]),
        new MarkerProperties(generateID(), true, Date.now().toString(), 0)
      );
      markers.push(endMarker);

      // Create linestring
      const lineStringFeature = LineStringFeature.coordsOnlyFeature(coordinates);
      lineStrings.push(lineStringFeature);
    }

    // Create new route with imported data. Position must be sequential —
    // importing a multi-day hike calls this once per day, and a hardcoded
    // position would leave stage ordering (and connectors) ambiguous.
    const newRoute = RouteFactory.fromLegacyData(
      markers,
      lineStrings,
      trailName,
      Object.keys(state.routes).length, // position: append after existing routes
      plannedTrailId
        ? { plannedTrailId, referencedHikes, day, route_style }
        : (day || route_style ? { day, route_style } : undefined)
    );

    commit('addRoute', newRoute);

    // Imports leave everything DESELECTED — the whole trail renders in full
    // colour and nothing is active (multi-day imports call this once per
    // day, and auto-selecting would leave the last day active).
    commit('clearActiveRoute');
    await dispatch('addAllRoutesToMap');

    // Fit the camera to the WHOLE plan — multi-day imports call this action
    // once per day, so fitting only the imported geoJson would leave the
    // camera parked on the final day.
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    const extend = (coords: number[]) => {
      if (!coords || coords.length < 2) return;
      if (coords[0] < minLng) minLng = coords[0];
      if (coords[0] > maxLng) maxLng = coords[0];
      if (coords[1] < minLat) minLat = coords[1];
      if (coords[1] > maxLat) maxLat = coords[1];
    };
    for (const route of Object.values(state.routes)) {
      route.markers.forEach((m: any) => extend(m.markerGeometry.coordinates));
      route.lineStrings.forEach((ls: any) =>
        ls.lineStringGeometry.coordinates.forEach(extend)
      );
    }
    if (Number.isFinite(minLng) && Number.isFinite(minLat)) {
      // @ts-ignore
      const map = getMap();
      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
        padding: computeMapFitPadding(map),
        duration: 600
      });
    }
  },

  async uploadImages({ state }: PlanTrailActionContext, data: FileUploadData) {
    const files = data.files;
    for (const file of files) {
      state.photosToUpload.push(file);
    }

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const id = data.ids[i];

        // Calculate MD5 checksum
        // We'll use a simple approach that works for ActiveStorage
        const arrayBuffer = await file.arrayBuffer();
        const hashBase64 = await calculateMD5Base64(arrayBuffer);

        // Step 1: Generate direct upload credentials
        const response = await graphClient.request(GenerateDirectUpload, {
          input: {
            filename: file.name,
            byteSize: file.size,
            contentType: file.type || 'image/jpeg',
            checksumBase64: hashBase64,
            serviceName: window.publicBucketService
          }
        });

        const { uploadUrl, headers, signedId } = response.generateDirectUpload;

        // Step 2: Upload file directly to storage
        const uploadHeaders = typeof headers === 'string' ? JSON.parse(headers) : headers;

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            ...uploadHeaders,
            'Content-Type': file.type || 'image/jpeg'
          },
          body: file,
          credentials: 'omit'
        });

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          console.error('Upload failed:', uploadResponse.status, errorText);
          throw new Error(`Upload failed: ${uploadResponse.statusText} - ${errorText}`);
        }

        // Step 3: Store the signed ID with the photo ID
        const index = state.photosToUpload.findIndex(fileToCheck => fileToCheck.name == file.name);
        if (index !== -1) {
          state.photosToUpload.splice(index, 1);
        }
        state.photosIds.push(id);
        state.photoSignedIds[id] = signedId;
      }
    } catch (e) {
      alert(e);
    }
  },

  destroyImage({ state }: PlanTrailActionContext, data: ImageDestroyData) {
    state.photosToUpload = state.photosToUpload.filter(file => file.name !== data.file.name);
    state.photosIds = state.photosIds.filter(photoId => photoId !== data.id);
    delete state.photoSignedIds[data.id];
    state.photosMarkedForRemoval = state.photosMarkedForRemoval.filter(photoId => photoId !== data.id);
    // If you had a GraphQL destroyImage mutation, you might call it here
    // graphClient.request(DestroyImage, { id: id }).then(data => { /* handle response */ })
  },

  async addTracerRoute({ state }: PlanTrailActionContext, fileOrContent: File | string) {
    try {
      let content: string;
      if (fileOrContent instanceof File) {
        content = await fileOrContent.text();
      } else {
        content = fileOrContent;
      }

      // Detect file type and convert to GeoJSON
      let geoJson: any;

      // Check if it's already GeoJSON
      if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
        try {
          geoJson = JSON.parse(content);
          // Validate it's a valid GeoJSON
          if (!geoJson.type) {
            throw new Error('Invalid GeoJSON: missing type property');
          }
        } catch (e) {
          throw new Error('Failed to parse GeoJSON file');
        }
      }
      // Check if it's KML
      else if (content.includes('<kml') || content.includes('<kml:')) {
        const parser = new DOMParser();
        const xml = parser.parseFromString(content, 'text/xml');

        // Check for parsing errors
        const parseError = xml.querySelector('parsererror');
        if (parseError) {
          throw new Error('Failed to parse KML file: invalid XML');
        }

        // Convert KML to GeoJSON
        geoJson = toGeoJson.kml(xml);
      }
      // Otherwise assume it's GPX
      else {
        geoJson = gpxFileToGeoJsonHelper(content);
      }

      // Store the GeoJSON in state for re-rendering on style change
      state.tracerRouteGeoJson = geoJson;

      // Render the tracer route
      renderTracerRoute(geoJson);

      const bbox = turfBbox(geoJson);
      // @ts-ignore
      const map = getMap();
      map.fitBounds(bbox, { padding: 20 });
    } catch (e) {
      alert(gpxImportErrorMessage(e));
      console.error(e);
    }
  },

  // Re-add tracer route after map style change
  reAddTracerRoute({ state }: PlanTrailActionContext) {
    if (!state.tracerRouteGeoJson) {
      return;
    }
    renderTracerRoute(state.tracerRouteGeoJson);
  }
};

// Helper function to render tracer route on map (used by both addTracerRoute and reAddTracerRoute)
function renderTracerRoute(geoJson: any) {
  const map = getMap();
  const layerRegistry = getLayerRegistry();
  const sourceId = 'tracer-route';
  const layerId = 'tracer-route';
  const pointsSourceId = 'tracer-route-points';
  const pointsLayerId = 'tracer-route-points';

  // Remove existing layers and sources
  layerRegistry.removeLayer(layerId);
  if (map.getLayer(pointsLayerId)) {
    map.removeLayer(pointsLayerId);
  }
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }
  if (map.getSource(pointsSourceId)) {
    map.removeSource(pointsSourceId);
  }

  // Add the line source and layer
  layerRegistry.addSource(sourceId, {
    type: 'geojson',
    data: geoJson
  });

  layerRegistry.addLayer({
    id: layerId,
    type: 'line',
    source: sourceId,
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': '#5708ea',
      'line-width': 4,
      'line-opacity': 0.5
    }
  }, MapLayerCategory.BaseAnnotations, { z: 5 });

  // Extract start and end points from the GeoJSON
  let startPoint: [number, number] | null = null;
  let endPoint: [number, number] | null = null;

  if (geoJson.features && geoJson.features.length > 0) {
    for (const feature of geoJson.features) {
      if (feature.geometry?.type === 'LineString' && feature.geometry.coordinates?.length > 0) {
        const coords = feature.geometry.coordinates;
        if (!startPoint) {
          startPoint = [coords[0][0], coords[0][1]];
        }
        endPoint = [coords[coords.length - 1][0], coords[coords.length - 1][1]];
      }
    }
  }

  // Add start and end point markers if we found coordinates
  if (startPoint || endPoint) {
    const pointFeatures: GeoJSON.Feature[] = [];

    if (startPoint) {
      pointFeatures.push({
        type: 'Feature',
        properties: { type: 'start' },
        geometry: { type: 'Point', coordinates: startPoint }
      });
    }

    if (endPoint) {
      pointFeatures.push({
        type: 'Feature',
        properties: { type: 'end' },
        geometry: { type: 'Point', coordinates: endPoint }
      });
    }

    const pointsGeoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: pointFeatures
    };

    // Add source and layer directly to map (bypassing layerRegistry to avoid hover interactions)
    map.addSource(pointsSourceId, {
      type: 'geojson',
      data: pointsGeoJson
    });

    map.addLayer({
      id: pointsLayerId,
      type: 'circle',
      source: pointsSourceId,
      paint: {
        'circle-radius': 7,
        'circle-color': '#5708ea',
        'circle-opacity': 0.4,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-opacity': 0.5
      }
    });
  }
}
